<template>
<div>
  <!-- Grid layout pour les articles avec carte spéciales -->
  <v-row>
    <!-- Éléments (Articles, Pubs, Weather) -->
    <v-col
      v-for="(item, index) in gridItems"
      :key="item.id || 'item-'+index"
      cols="12"
      sm="6"
      md="4"
      lg="3"
      :class="{'mb-4': true, [getColClass(item)]: true}"
    >
      <!-- Article Card (élément standard) -->
      <article-card
        v-if="item.type === 'article'"
        :article="item.content"
        :label="item.subtype || null"
        :size="getCardSize(item)"
        :class="getCardSizeClass(item)"
        @click="recordInteraction(item, 'click', index)"
        @view="recordInteraction(item, 'view', index)"
      />
      
      <!-- Ad Card (publicité) -->
      <v-card 
        v-else-if="item.type === 'ad'"
        class="ad-card" 
        elevation="1" 
        height="100%"
        @click="recordAdClick(item, index)"
      >
        <div class="ad-header">
          <v-icon size="small" class="mr-2">mdi-advertisement</v-icon>
          Publicité
        </div>
        <div class="ad-content">
          <v-img 
            v-if="item.content && item.content.imageUrl" 
            :src="item.content.imageUrl" 
            height="120" 
            contain 
            class="mb-3"
          />
          <v-icon v-else size="x-large" class="mb-3">{{ getRandomAdIcon() }}</v-icon>
          <h3 v-if="item.content" class="text-subtitle-2 font-weight-medium">{{ item.content.title }}</h3>
          <p v-if="item.content" class="text-caption">{{ item.content.message }}</p>
          <p v-else>Site officiel - Fait pour vous</p>
        </div>
      </v-card>
      
      <!-- Weather Card (widget météo) -->
      <weather-widget
        v-else-if="item.type === 'weather'"
        :location="weatherLocation"
        :temperature="weatherData.temperature"
        :condition="weatherData.condition"
        :weather-info="weatherData.info"
      />
    </v-col>
  </v-row>
        
  <!-- Indicateur de chargement -->
  <v-row v-if="loading">
    <v-col class="text-center py-8">
      <v-progress-circular indeterminate color="primary" :size="50" :width="5"></v-progress-circular>
    </v-col>
  </v-row>
      
  <!-- Message "Aucun article" -->
  <v-row v-if="!loading && gridItems.length === 0">
    <v-col class="text-center py-8 text-medium-emphasis">
      <v-icon size="large" color="grey">mdi-alert-circle-outline</v-icon>
      <p class="mt-2">Aucun article disponible</p>
    </v-col>
  </v-row>
</div>
</template>

<script>
import axios from 'axios';
import ArticleCard from './ArticleCard.vue';
import WeatherWidget from '@/components/weather/WeatherWidget.vue';
import appService from '@/services/appService';

export default {
  name: 'ArticleGrid',
  components: {
    ArticleCard,
    WeatherWidget
  },
  props: {
    gridItems: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'standard',
      validator: (value) => ['standard', 'mySources', 'trending', 'category'].includes(value)
    }
  },
  data() {
    return {
      weatherLocation: 'Paris',
      weatherData: {
        temperature: 18,
        condition: 'partly-cloudy',
        info: 'Qualité de l\'air: Bonne'
      }
    };
  },
  computed: {
    // Obtient les préférences utilisateur pour les recommandations
    userPreferences() {
      return {
        preferredCategories: this.$store.state.ui?.preferredCategories || [],
        followedSources: this.$store.state.ui?.followedSources || [],
        readingHistory: this.$store.state.ui?.readingHistory || []
      };
    }
  },
  created() {
    // Essayez de charger la météo lors de la création du composant
    this.loadWeatherData();
  },
  methods: {
    // Détermine la classe de taille pour la carte (petit, moyen, grand)
    getCardSizeClass(item) {
      if (!item.layout) return '';
      
      return item.layout;
    },
    
    // Détermine la taille de la carte pour la prop 'size'
    getCardSize(item) {
      if (!item.layout) return 'small';
      
      if (item.layout === 'card-large') return 'large';
      if (item.layout === 'card-medium') return 'medium';
      return 'small';
    },
    
    // Détermine la classe de largeur pour la colonne
    getColClass(item) {
      // Par défaut, chaque élément prend sa taille normale
      if (!item.layout) return '';
      
      // Les cartes larges prennent 2 colonnes
      if (item.layout === 'card-large') {
        return 'md-6 lg-6';
      }
      
      // Les cartes moyennes prennent 1.5 colonnes
      if (item.layout === 'card-medium') {
        return 'md-4 lg-4';
      }
      
      return '';
    },
    
    // Enregistre une interaction avec un article ou une publicité
    recordInteraction(item, interactionType, position) {
      if (!item || !item.content) return;
      
      // Pour les articles, enregistrer les vues/clics
      if (item.type === 'article') {
        // Enregistrer localement via appService
        if (interactionType === 'click') {
          appService.recordArticleView(item.content);
        }
        
        // Envoyer au backend via l'API
        this.sendInteraction({
          itemType: 'article',
          itemId: item.content._id,
          interactionType,
          position
        });
      }
    },
    
    // Gère le clic sur une publicité
    recordAdClick(item, position) {
      if (!item || !item.content) return;
      
      // Envoyer au backend via l'API
      this.sendInteraction({
        itemType: 'ad',
        itemId: item.content._id,
        interactionType: 'click',
        position
      });
      
      // Rediriger vers l'URL cible
      if (item.content.targetUrl) {
        window.open(item.content.targetUrl, '_blank');
      }
    },
    
    // Envoie l'interaction au backend
    async sendInteraction(data) {
      try {
        await axios.post('/api/grid/interaction', data);
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement de l\'interaction:', error);
      }
    },
    
    // Charge les données météo (pourrait venir d'une API externe)
    async loadWeatherData() {
      try {
        // Dans une implémentation réelle, cela pourrait être une API météo
        // Pour l'instant, nous utilisons des données statiques
        this.weatherLocation = 'Paris';
        this.weatherData = {
          temperature: Math.floor(Math.random() * 15) + 10, // Entre 10 et 25°C
          condition: ['sunny', 'partly-cloudy', 'cloudy', 'rainy'][Math.floor(Math.random() * 4)],
          info: 'Qualité de l\'air: Bonne'
        };
      } catch (error) {
        console.error('Erreur lors du chargement des données météo:', error);
      }
    },
    
    // Génère aléatoirement une icône pour les publicités
    getRandomAdIcon() {
      const icons = [
        'mdi-shopping-outline', 
        'mdi-car', 
        'mdi-home', 
        'mdi-cellphone', 
        'mdi-briefcase'
      ];
      return icons[Math.floor(Math.random() * icons.length)];
    }
  }
};
</script>

<style lang="scss" scoped>
.ad-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 3px 3px -2px rgba(0,0,0,0.2), 
                0 3px 4px 0 rgba(0,0,0,0.14),
                0 1px 8px 0 rgba(0,0,0,0.12) !important;
  }
  
  .ad-header {
    padding: 12px 16px;
    border-bottom: 1px solid rgba(0,0,0,0.12);
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(0,0,0,0.6);
    display: flex;
    align-items: center;
  }
  
  .ad-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px;
    text-align: center;
    color: rgba(0,0,0,0.6);
  }
}

// Classes pour différentes tailles de cartes
.card-small {
  // Taille par défaut, pas besoin de styles supplémentaires
}

.card-medium {
  // Les cartes moyennes sont un peu plus grandes
}

.card-large {
  // Les cartes larges prennent plus d'espace
}
</style>